import { Fragment, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, ChevronDown, ChevronRight, Loader2, Lock } from "lucide-react";
import { UnlockDialog } from "./RuleSetDialogs";
import GlowCard from "../components/GlowCard";
import StatusBadge from "../components/StatusBadge";
import ColumnFilter from "../components/ColumnFilter";
import ConfirmDialog from "../StandardsLibrary/ConfirmDialog";
import AwpRuleDetail from "./AwpRuleDetail";
import {
  AwpRule,
  AwpRuleSet,
  RULE_LAYERS,
  RuleSetStatus,
  deleteRule,
  listRules,
  updateRuleSet,
} from "./rulesApi";

export const STATUS_TONE: Record<RuleSetStatus, "pass" | "warning" | "info"> = {
  active: "pass",
  draft: "warning",
  retired: "info",
};
export const STATUS_LABEL: Record<RuleSetStatus, string> = {
  active: "Active",
  draft: "Draft",
  retired: "Retired",
};

type FilterColumn = "domain" | "types" | "method" | "severity" | "gate";
const FILTER_COLUMNS: FilterColumn[] = ["domain", "types", "method", "severity", "gate"];
const NO_FILTERS: Record<FilterColumn, string[]> = { domain: [], types: [], method: [], severity: [], gate: [] };

/** The value(s) a rule shows in each filterable column. Types is the one with several. */
const VALUES: Record<FilterColumn, (r: AwpRule) => string[]> = {
  domain: (r) => [r.domain],
  types: (r) => r.packageTypes,
  method: (r) => [r.method],
  severity: (r) => [r.severity],
  gate: (r) => [r.gate],
};

/** Reading order for columns where alphabetical would be wrong. Unlisted values sort after. */
const ORDER: Partial<Record<FilterColumn, string[]>> = {
  types: ["CWP", "EWP", "PWP"],
  severity: ["Critical", "Major", "Moderate", "Minor", "Advisory"],
  gate: ["DQ0", "DQ1", "DQ2", "DQ3"],
};
const rank = (order: string[], v: string) => {
  const i = order.indexOf(v);
  return i < 0 ? order.length : i;
};

interface AwpRuleSetViewProps {
  set: AwpRuleSet;
  readOnly?: boolean;
  onBack: () => void;
  onChanged: (set: AwpRuleSet) => void;
}

/**
 * One AWP standard opened: its rules in a filterable table, a row opening to the full requirement
 * and the evidence it asks for. Reached from the library the way an engineering standard's workbench
 * is — the list stays a list, this is the page for reading one.
 */
const AwpRuleSetView = ({ set, readOnly = false, onBack, onChanged }: AwpRuleSetViewProps) => {
  const [rules, setRules] = useState<AwpRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openRule, setOpenRule] = useState<string | null>(null);
  /** Ticked values per filterable column. An empty list is no filter on that column. */
  const [filters, setFilters] = useState<Record<FilterColumn, string[]>>(NO_FILTERS);
  const [query, setQuery] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<AwpRule | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const removeRule = async (rule: AwpRule) => {
    setDeletingId(rule.id);
    setError(null);
    try {
      onChanged(await deleteRule(set.id, rule.id));
      setRules((prev) => (prev ?? []).filter((x) => x.id !== rule.id));
      setOpenRule(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove the rule.");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const list = await listRules(set.id);
        if (live) setRules(list);
      } catch (err) {
        if (live) {
          setError(err instanceof Error ? err.message : "Could not load the rules.");
          setRules([]);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [set.id]);

  // Each column's values with how many rules carry them. Domains keep document order; the rest sort
  // by how a reviewer reads them (severity worst first, gate earliest first).
  const options = useMemo(() => {
    const out = {} as Record<FilterColumn, { value: string; count: number }[]>;
    for (const col of FILTER_COLUMNS) {
      const counts = new Map<string, number>();
      for (const r of rules ?? []) for (const v of VALUES[col](r)) counts.set(v, (counts.get(v) ?? 0) + 1);
      const values = Array.from(counts.keys());
      const order = ORDER[col];
      if (order) values.sort((a, b) => rank(order, a) - rank(order, b) || a.localeCompare(b));
      else if (col !== "domain") values.sort();
      out[col] = values.map((value) => ({ value, count: counts.get(value)! }));
    }
    return out;
  }, [rules]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rules ?? []).filter(
      (r) =>
        FILTER_COLUMNS.every((col) => {
          const picked = filters[col];
          return picked.length === 0 || VALUES[col](r).some((v) => picked.includes(v));
        }) && (!q || `${r.code} ${r.title} ${r.requirement}`.toLowerCase().includes(q)),
    );
  }, [rules, filters, query]);

  const filtered = FILTER_COLUMNS.some((col) => filters[col].length > 0);
  const header = (col: FilterColumn, label: string) => (
    <th>
      <ColumnFilter
        label={label}
        options={options[col]}
        selected={filters[col]}
        onChange={(next) => setFilters((f) => ({ ...f, [col]: next }))}
      />
    </th>
  );

  const setStatus = async (status: RuleSetStatus) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await updateRuleSet(set.id, { status }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change the status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vr-page vr-page-fixed">
      <div className="vr-page-head">
        <div>
          <button className="vr-rowbtn" onClick={onBack} style={{ marginBottom: ".75rem" }}>
            <ArrowLeft size={12} /> Standards
          </button>
          <h1 className="vr-h-page">
            {set.title} <StatusBadge status={STATUS_TONE[set.status]} label={STATUS_LABEL[set.status]} />
          </h1>
          <p style={{ marginTop: ".25rem" }}>
            {[
              RULE_LAYERS.find((l) => l.key === set.layer)?.label,
              set.docNo,
              set.version,
              `${set.ruleCount} rules`,
              set.source.fileName,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        {/* The audit trail is read from the library list, on the admin side only — not from here. */}
        <div className="vr-head-actions">
        {!readOnly && (
          <>
            {set.status !== "active" && (
              <button className="vr-btn vr-btn-glow" disabled={busy} onClick={() => void setStatus("active")}>
                {busy && <Loader2 size={14} className="vr-spin-inline" />} Activate
              </button>
            )}
            {set.status === "active" && (
              // Unlock takes an active standard back to Draft — after asking why. See UnlockDialog.
              <button
                className="vr-btn vr-btn-outline"
                disabled={busy}
                onClick={() => setUnlocking(true)}
                title="Unlock — take this standard back to Draft"
              >
                <Lock size={14} /> Unlock
              </button>
            )}
            {set.status === "retired" && (
              <button className="vr-btn vr-btn-outline" disabled={busy} onClick={() => void setStatus("draft")}>
                {busy && <Loader2 size={14} className="vr-spin-inline" />} Back to draft
              </button>
            )}
          </>
        )}
        </div>
      </div>

      {unlocking && (
        <UnlockDialog
          set={set}
          onCancel={() => setUnlocking(false)}
          onUnlock={async (reason) => {
            onChanged(await updateRuleSet(set.id, { status: "draft", reason }));
            setUnlocking(false);
          }}
        />
      )}

      {error && <div className="vr-form-error">{error}</div>}

      {set.problems.length > 0 && (
        <div className="vr-awp-readout vr-awp-readout-warn">
          <AlertTriangle size={14} />
          <span>
            When this was read: {set.problems.slice(0, 3).join(" ")}
            {set.problems.length > 3 ? ` (+${set.problems.length - 3} more)` : ""}
          </span>
        </div>
      )}

      <GlowCard className="vr-table-card vr-awp-rules-card" hover={false}>
        {/* Column filters live in the headings below; this bar keeps only search and the count. */}
        <div className="vr-awp-filters">
          {filtered && (
            <button type="button" className="vr-rowbtn" onClick={() => setFilters(NO_FILTERS)}>
              Clear filters
            </button>
          )}
          <input
            className="vr-input vr-awp-filter"
            placeholder="Search code or text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className="vr-cell-code">
            {visible.length} of {rules?.length ?? 0}
          </span>
        </div>

        <div className="vr-table-scroll">
          <table className="vr-table">
            <thead>
              <tr>
                <th style={{ width: "1.5rem" }} />
                <th>Code</th>
                <th>Title</th>
                {header("domain", "Domain")}
                {header("types", "Types")}
                {header("method", "Method")}
                {header("severity", "Severity")}
                {header("gate", "Gate")}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const open = openRule === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      className={`vr-row-click${open ? " vr-row-open" : ""}`}
                      onClick={() => setOpenRule(open ? null : r.id)}
                    >
                      <td>{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                      <td className="vr-cell-title">
                        <strong>{r.code}</strong>
                      </td>
                      <td>{r.title}</td>
                      <td className="vr-cell-dim">{r.domain}</td>
                      <td>
                        {r.packageTypes.map((t) => (
                          <span key={t} className={`vr-pkg-type vr-pkg-${t.toLowerCase()}`} style={{ marginRight: ".25rem" }}>
                            {t}
                          </span>
                        ))}
                      </td>
                      <td className="vr-cell-dim">{r.method}</td>
                      <td>
                        <span className={`vr-awp-sev vr-awp-sev-${r.severity.toLowerCase()}`}>{r.severity}</span>
                      </td>
                      <td className="vr-cell-dim">{r.gate}</td>
                    </tr>
                    {open && (
                      <tr className="vr-row-open">
                        <td />
                        <td colSpan={7}>
                          <AwpRuleDetail
                            rule={r}
                            // Active is locked: no edit or delete until the standard is unlocked.
                            readOnly={readOnly || set.status === "active"}
                            deleting={deletingId === r.id}
                            onSaved={(saved) =>
                              setRules((prev) => (prev ?? []).map((x) => (x.id === saved.id ? saved : x)))
                            }
                            onDelete={setConfirmDelete}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {rules === null && (
          <div className="vr-empty">
            <Loader2 size={16} className="vr-spin-inline" /> Loading rules…
          </div>
        )}
        {rules !== null && visible.length === 0 && <div className="vr-empty">No rules match.</div>}
      </GlowCard>

      {confirmDelete && (
        <ConfirmDialog
          title="Remove this rule?"
          tone="danger"
          confirmLabel="Remove rule"
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const target = confirmDelete;
            setConfirmDelete(null);
            void removeRule(target);
          }}
        >
          <p className="vr-confirm-lead">
            <strong>{confirmDelete.code}</strong> — {confirmDelete.title}
          </p>
          <p>
            It is taken out of this standard and will not be checked. Re-importing the document is the only
            way to get it back.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default AwpRuleSetView;
